import difference from 'lodash/difference';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';
import moment from 'moment';
import { createSelector } from 'reselect';
import { showCohortEvents, showConversionEvents } from '../customEvents/utils';
import { PERMISSION_KEY_ASSIST } from '../reducers/reportsConfig/baseReportConfig';
import { getFields, getFilters } from '../selectors/fields';
import { getTranslate } from '../selectors/locale';
import rules from '../selectors/reportsConfig/fieldVisibilityRules';
import { getAdminModeEnabled, getIsAgency, getUserData, getUserPermissionDimensions } from '../selectors/user';
import {
    DELETABLE_GROUPS,
    EXCLUDED_METRICS_SELECTION_PAGE,
    getRoleDisplayName,
    getUserDisplayName,
    getUserRole,
    INITIAL_FIELD_TYPES,
    MEMBER_GROUP_TYPES,
    MEMBER_TYPES,
    RESTRICTED_ROLES,
    SHELF_STEPS,
    WIZARD_MODES,
} from './utils';
import {
    AGENCY_PERMISSION_DIMENSIONS,
    dataRestrictionsStateToApiFormat,
    SDK_EVENT_DIMENSION,
} from '../utils/agencyPermissions';
import { IN_OPERATOR, NOT_IN_OPERATOR } from '../utils/reportsConsts';
import { SAN_PARTNERS } from '../partnerConfiguration/consts';
import { getOrgAgencies } from '../selectors/agencies';

const getPageLoaded = state => state.teamManagement.pageLoaded;
const getUsers = state => state.teamManagement.orgUsers;
const getMessages = state => state.teamManagement.messages;
const getMessagesDefs = state => state.teamManagement.messagesDefs;
const getShelf = state => state.teamManagement.shelf;
const getPage = state => state.teamManagement.page;
const getAgencyDialog = state => state.teamManagement.agencyDialog;
const getEditMemberId = state => state.teamManagement.editMemberId;
const getUserRegistrationForm = state => state.teamManagement.userRegistrationForm;
const getRoleSelectionForm = state => state.teamManagement.roleSelectionForm;
const getAgencySelectionForm = state => state.teamManagement.agencySelectionForm;
const getSelectedMetrics = state => state.teamManagement.selectedMetrics;
const getScreenSelectionForm = state => state.teamManagement.screenSelectionForm;
const getSaveMemberError = state => state.teamManagement.saveMemberError;
const getWizard = state => state.teamManagement.wizard;
const getUans = state => state.teamManagement.uans;
const getScreensDict = state => state.teamManagement.screensDict;
const getSelectedDimensionPermissionValues = state => state.teamManagement.dimensionFilters;
const getDataRestrictions = state => state.teamManagement.dataRestrictions;
const getSectionPermissions = state => state.teamManagement.sectionPermissions;
const getSectionsDict = state => state.teamManagement.sectionsDict;
const getConfigurationRequiredDialog = state => state.teamManagement.configurationRequiredDialog;
export const getActiveMemberType = state => state.teamManagement.activeMemberType;
export const getAppsList = state => state.teamManagement.appsList;
export const getPartners = state => state.teamManagement.partners;
export const getConfirmChangesDialogState = state => state.teamManagement.confirmChangesDialog;
export const getSdkEvents = state => state.teamManagement.sdkEvents;

const getFieldTypes = createSelector(
    [getUserData, getActiveMemberType, getAgencySelectionForm, getOrgAgencies],
    (userData, activeMemberType, agencySelectionForm, orgAgencies) => {
        const {
            event_slot_analysis_types: eventSlotAnalysisTypes = [],
            feature_flags: featureFlags = [],
            permission_keys: permissionKeys = [],
        } = userData || {};

        const isEditingAgency = activeMemberType === MEMBER_TYPES.AGENCY && agencySelectionForm?.id;
        let permissionKeysToUse = permissionKeys;
        if (isEditingAgency) {
            const selectedAgency = orgAgencies?.find(a => a.agency_id === agencySelectionForm.id);
            if (selectedAgency) {
                permissionKeysToUse = selectedAgency.permission_keys;
            } else {
                permissionKeysToUse = agencySelectionForm.permission_keys || [];
            }
        }

        const fieldTypes = [];

        if (showCohortEvents(eventSlotAnalysisTypes)) {
            fieldTypes.push({ type: 'customEvents', label: 'STATIC.CUSTOM_EVENTS' });
        }
        if (showConversionEvents(eventSlotAnalysisTypes)) {
            fieldTypes.push({ type: 'conversionEvents', label: 'STATIC.CONVERSION_EVENTS' });
        }
        if (featureFlags && featureFlags.includes('cross-device')) {
            fieldTypes.push({ type: 'crossDeviceMetrics', label: 'STATIC.CROSS_DEVICE_METRICS' });
        }
        if (featureFlags && featureFlags.includes('web_customer')) {
            fieldTypes.push({ type: 'webMetrics', label: 'STATIC.WEB_METRICS' });
            fieldTypes.push({ type: 'appMetrics', label: 'STATIC.APP_METRICS' });
        }
        if (permissionKeysToUse && permissionKeysToUse.includes(PERMISSION_KEY_ASSIST)) {
            fieldTypes.push({ type: 'assistMetrics', label: 'STATIC.ASSIST_METRICS' });
        }
        return INITIAL_FIELD_TYPES.concat(fieldTypes);
    }
);

const _buildUserSearchString = user => {
    if (user.role && user.role.description) {
        return `${user.username};${user.first_name};${user.last_name};${user.email};${
            user.role.description
        };${getUserRole(user)}`.toLowerCase();
    }
    return `${user.username};${user.first_name};${user.last_name};${user.email};${getUserRole(user)}`.toLowerCase();
};

const organicSource = {
    name: 'organic',
    displayName: 'Organic',
    id: 'organic',
    icon: 'https://cdn.singular.net/dashboard/logo_path/0ac00edfda169d777c0b4290541adfc0a6ccdd41',
    adn_id: 434,
    display_name: 'Organic',
    logo_path: 'https://cdn.singular.net/dashboard/logo_path/0ac00edfda169d777c0b4290541adfc0a6ccdd41',
};

const getUserGroup = userStatus => {
    if (userStatus === 'Member') {
        return MEMBER_GROUP_TYPES.active;
    }

    if (userStatus === 'invite_sent' || userStatus === 'invite_opened') {
        return MEMBER_GROUP_TYPES.pending;
    }

    return MEMBER_GROUP_TYPES.inactive;
};

const getUsersByGroup = createSelector([getUsers, getTranslate, getUserData], (users, translate, userData) => {
    const groups = users.reduce(
        (total, user) => {
            const group = getUserGroup(user.status);
            const isReInviteLessThan24h =
                user.last_re_invited_at && moment().diff(moment(user.last_re_invited_at * 1000), 'hours') < 24;
            const userId = user.organization_user_id || user.username;
            const isMe = userId === userData.organization_user_id;

            const newUser = {
                ...user,
                id: userId,
                isMe,
                name: getUserDisplayName(user),
                description: user.role && user.role.description ? user.role.description : '',
                roleText: getUserRole(user),
                searchText: _buildUserSearchString(user),
                deletable: DELETABLE_GROUPS.includes(group),
                disabled: isReInviteLessThan24h || user.disabled,
                disabledTootlip:
                    isReInviteLessThan24h || user.disabled
                        ? translate('STATIC.PAGES.TEAM_MEMBERS.REINVITE_DISABLED_TOOLTIP')
                        : '',
            };
            total[group].push(newUser);
            return total;
        },
        {
            [MEMBER_GROUP_TYPES.pending]: [],
            [MEMBER_GROUP_TYPES.active]: [],
            [MEMBER_GROUP_TYPES.inactive]: [],
        }
    );

    // sort pending list according to creation time.
    groups.pending.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return groups;
});

const getAgenciesByGroup = createSelector([getOrgAgencies], agencies => {
    return {
        [MEMBER_GROUP_TYPES.active]: (agencies || []).map(agency => ({
            id: agency.agency_id,
            name: agency.display_name,
            imgSrc: agency.company_logo,
            role: agency.role,
            permission_keys: agency.permission_keys,
            deletable: true,
            searchText: `${agency.display_name};${agency.agency_id}`.toLowerCase(),
        })),
    };
});

const getMemberTypes = createSelector(
    [getUserData, getUsersByGroup, getAgenciesByGroup],
    (userData, usersByGroup, agenciesByGroup) => {
        const categories = [{ type: MEMBER_TYPES.USER, count: usersByGroup.active?.length || 0 }];
        if (userData.is_agencies_allowed) {
            categories.push({ type: MEMBER_TYPES.AGENCY, count: agenciesByGroup.active?.length || 0 });
        }
        return categories;
    }
);

const getMembersByGroup = createSelector(
    [getUsersByGroup, getAgenciesByGroup, getActiveMemberType],
    (users, agencies, activeMemberType) => (activeMemberType === MEMBER_TYPES.USER ? users : agencies)
);

const getGroups = createSelector(
    [getMembersByGroup, getMessages, getMessagesDefs, getAdminModeEnabled, getActiveMemberType],
    (membersByGroup, messages, messagesDefs, adminModeEnabled, activeMemberType) => {
        return Object.keys(membersByGroup)
            .filter(group => {
                if (!adminModeEnabled && group === MEMBER_GROUP_TYPES.inactive) {
                    return false;
                }

                if (activeMemberType === MEMBER_TYPES.AGENCY && group === MEMBER_GROUP_TYPES.active) {
                    return true;
                }

                return membersByGroup[group].length;
            })
            .map(group => ({
                name: group,
                displayName: `STATIC.PAGES.TEAM_MEMBERS.GROUP_NAME_${group.toUpperCase()}_${activeMemberType}`,
                infoMessages: messages[group] ? messages[group].map(messageId => messagesDefs[messageId]) : [],
            }));
    }
);

export const getPermissionDimensions = createSelector(
    [
        getUserPermissionDimensions,
        getFilters,
        getSelectedDimensionPermissionValues,
        getAppsList,
        getUans,
        getPartners,
        getActiveMemberType,
    ],
    (userPermissionDimensions, apiFilters, selectedValues, apps, uans, partners, activeMemberType) => {
        const memberPermissionDimensions =
            activeMemberType === MEMBER_TYPES.AGENCY ? AGENCY_PERMISSION_DIMENSIONS : userPermissionDimensions;

        const permissionDimensions = {};
        memberPermissionDimensions.forEach(dimensionName => {
            // App values are stored by id and not name
            if (dimensionName === 'app') {
                permissionDimensions.app = {
                    name: 'app',
                    values: (apps || []).map(app => ({
                        name: app.id,
                        displayName: app.name,
                        icon: app.icon,
                    })),
                    selected: selectedValues.app || { values: [], addFuture: false },
                };
                return;
            }

            // App site values are stored by id and not name
            if (dimensionName === 'app_site_id') {
                permissionDimensions.app_site_id = {
                    name: 'app_site_id',
                    values: (apps || []).flatMap(app =>
                        app.sites.map(site => ({
                            name: site.publicId,
                            displayName: site.name,
                            app: {
                                name: app.id,
                                displayName: app.name,
                                icon: app.icon,
                            },
                        }))
                    ),
                    selected: selectedValues.app_site_id || { values: [], addFuture: false },
                };
                return;
            }

            // Source values are produced from uans and not the source filter (one day it will if we fix the source
            // filter to only show properly source values)
            if (dimensionName === 'base_source') {
                const baseSources = activeMemberType === MEMBER_TYPES.AGENCY ? partners : Object.values(uans);
                permissionDimensions.base_source = {
                    name: 'base_source',
                    values: baseSources.map(baseSource => ({
                        name: baseSource.name,
                        displayName: baseSource.display_name,
                        icon: baseSource.logo_path,
                    })),
                    selected: selectedValues.base_source || { values: [], addFuture: false },
                };
                return;
            }

            // All other dimensions we can filter on must have an API filter
            const filter = apiFilters.find(f => f.name === dimensionName);
            if (filter)
                permissionDimensions[dimensionName] = {
                    name: dimensionName,
                    displayName: filter.display_name,
                    values: filter.values.map(v => ({ name: v.name, displayName: v.display_name })),
                    selected: selectedValues[dimensionName] || { values: [], addFuture: false },
                };
        });
        return permissionDimensions;
    }
);

export const getPermissionDimension = dimensionName => {
    return createSelector([getPermissionDimensions], permissionDimensions => {
        return permissionDimensions[dimensionName];
    });
};

const getShelfSteps = createSelector(
    [getPermissionDimensions, getRoleSelectionForm],
    (permissionDimensions, roleSelectionForm) => {
        if (!RESTRICTED_ROLES.includes(roleSelectionForm.role)) {
            return [SHELF_STEPS.roleSelection, SHELF_STEPS.userDetails];
        }

        const shelfStepByDimension = {
            app: SHELF_STEPS.appSelection,
            base_source: SHELF_STEPS.sourceSelection,
        };

        const permissionSteps = Object.values(permissionDimensions).map(
            dimension => shelfStepByDimension[dimension.name] || dimension.name
        );

        return [
            SHELF_STEPS.roleSelection,
            SHELF_STEPS.userDetails,
            ...permissionSteps,
            SHELF_STEPS.metricsSelection,
            SHELF_STEPS.screenSelection,
            SHELF_STEPS.review,
        ];
    }
);

const getSummaryPages = createSelector([getShelfSteps, getWizard], (shelfSteps, wizard) => {
    return shelfSteps.filter(
        step =>
            step !== SHELF_STEPS.review &&
            // Dont show role selection in summary when we're creating a new user
            (wizard.mode === WIZARD_MODES.edit || step !== SHELF_STEPS.roleSelection)
    );
});

export const getEditedMember = createSelector([getEditMemberId, getMembersByGroup], (editMemberId, membersByGroup) => {
    if (!editMemberId) return null;

    const activeMembers = membersByGroup[MEMBER_GROUP_TYPES.active];
    const editedMember = activeMembers.find(member => member.id === editMemberId);

    return editedMember;
});

const getShelfFormatted = createSelector(
    [getShelf, getEditedMember, getRoleSelectionForm, getActiveMemberType],
    (shelf, editedMember, roleSelection, activeMemberType) => {
        const headerText = activeMemberType === MEMBER_TYPES.USER ? 'Invite New User' : 'Add Agency';

        return {
            ...shelf,
            headerText: editedMember ? `Edit ${editedMember.name}'s Permissions` : headerText,
            headerData: {
                roleType: getRoleDisplayName(roleSelection.role),
            },
        };
    }
);

export const getTeamManagementPage = createSelector(
    [
        getPageLoaded,
        getMembersByGroup,
        getGroups,
        getMemberTypes,
        getActiveMemberType,
        getPage,
        getAgencyDialog,
        getOrgAgencies,
        getConfirmChangesDialogState,
    ],
    (
        pageLoaded,
        membersByGroup,
        membersGroups,
        memberTypes,
        activeMemberType,
        page,
        agencyDialog,
        agencies,
        { open: confirmChangesDialogOpen }
    ) => {
        return {
            pageLoaded: pageLoaded && !!agencies,
            membersByGroup,
            membersGroups,
            memberTypes,
            activeMemberType,
            page,
            agencyDialog,
            confirmChangesDialogOpen,
        };
    }
);

export const getTeamManagementShelf = createSelector(
    [getShelfFormatted, getRoleSelectionForm, getWizard, getShelfSteps, getPermissionDimensions],
    (shelf, roleForm, wizard, shelfSteps, permissionDimensions) => {
        return {
            shelf,
            mode: wizard.mode,
            role: roleForm.role,
            shelfSteps,
            editedMember: wizard.userData,
            permissionDimensions,
        };
    }
);

const getWizardOverrideButtons = createSelector([getWizard], wizard => {
    let ret = {};
    if (wizard.currentActionType === WIZARD_MODES.edit) {
        ret = {
            next: {
                text: 'Continue',
                onClickArgs: {
                    skipCompleted: true,
                },
            },
            back: {
                visible: false,
            },
        };
    }
    return ret;
});

export const getUserRegistrationFormPage = createSelector(
    [
        getUserData,
        getUserRegistrationForm,
        getSaveMemberError,
        getWizard,
        getWizardOverrideButtons,
        getTranslate,
        getActiveMemberType,
    ],
    (userData, userRegistrationForm, saveMemberError, wizard, wizardOverrideButtons, translate, activeMemberType) => {
        const finishButtonText = translate(
            wizard.mode === WIZARD_MODES.edit
                ? 'STATIC.PAGES.TEAM_MEMBERS.WIZARD_UPDATE_PERMISSIONS_BUTTON'
                : `STATIC.BUTTONS.${activeMemberType}_CREATE_NEW_MEMBER`
        );
        return {
            username: userData.username,
            userRegistrationForm,
            saveMemberError,
            mode: wizard.mode,
            wizardOverrideButtons,
            translate,
            finishButtonText,
        };
    }
);

export const getAppSelectionPage = createSelector(
    [getAppsList, getPermissionDimension('app'), getWizardOverrideButtons, getTranslate],
    (apps, permissionDimension, wizardOverrideButtons, translate) => {
        return {
            apps: apps
                .map(a => ({ ...a, selected: (permissionDimension?.selected.values || []).includes(a.id) }))
                .sort((a, b) => a.name.localeCompare(b.name)),
            wizardOverrideButtons,
            translate,
        };
    }
);

export const getAppSitePageStep = createSelector(
    [getAppsList, getPermissionDimension('app_site_id'), getTranslate],
    (apps, permissionDimension, translate) => {
        return {
            apps: apps
                .map(app => ({
                    ...app,
                    selected: app.sites.every(site =>
                        (permissionDimension?.selected.values || []).includes(site.publicId)
                    ),
                    sites: app.sites.map(site => ({
                        ...site,
                        selected: (permissionDimension?.selected.values || []).includes(site.publicId),
                    })),
                }))
                .sort((a, b) => a.name.localeCompare(b.name)),
            translate,
        };
    }
);

export const getDisabledPartnerNames = createSelector(
    [getActiveMemberType, getUserData],
    (activeMemberType, userData) => {
        return activeMemberType === MEMBER_TYPES.AGENCY && !userData.san_partners_for_agency_supported
            ? Array.from(SAN_PARTNERS)
            : [];
    }
);

export const getPartnerPageStep = createSelector(
    [
        getPartners,
        getPermissionDimension('base_source'),
        getMembersByGroup,
        getGroups,
        getAgencySelectionForm,
        getAdminModeEnabled,
        getDisabledPartnerNames,
        getTranslate,
    ],
    (
        partners,
        permissionDimension,
        membersByGroup,
        membersGroups,
        agencySelectionForm,
        adminModeEnabled,
        disabledPartnerNames,
        translate
    ) => {
        const members = membersGroups.reduce((acc, group) => {
            if (
                group.name === MEMBER_GROUP_TYPES.pending ||
                (!adminModeEnabled && group.name === MEMBER_GROUP_TYPES.inactive)
            ) {
                return acc;
            }

            return [...acc, ...membersByGroup[group.name]];
        }, []);

        const membersPerPartner = members.reduce((acc, member) => {
            if (member.name === agencySelectionForm.name) {
                return acc;
            }

            const rowPermission = member.role.row.find(({ dimension }) => dimension === 'base_source') || {
                operator: 'not in',
                values: [],
            };

            const partnersToAdd =
                rowPermission.operator === IN_OPERATOR
                    ? rowPermission.values
                    : partners
                          .filter(partner => !rowPermission.values.includes(partner.name))
                          .map(partner => partner.name);

            partnersToAdd.forEach(partnerName => {
                acc[partnerName] = acc[partnerName] || [];
                acc[partnerName].push(member.name);
            });
            return acc;
        }, {});

        return {
            partners: partners.map(partner => ({
                ...partner,
                selected: (permissionDimension?.selected.values || []).includes(partner.name),
                icon: partner.logoPath,
                id: partner.name,
                disabled: disabledPartnerNames.includes(partner.name),
            })),
            membersPerPartner,
            translate,
        };
    }
);

export const getSectionsPageStep = createSelector(
    [getSectionPermissions, getSectionsDict, getRoleSelectionForm],
    (allSectionPermissions, sectionsDict, roleSelectionForm) => {
        return {
            allSectionPermissions,
            allSections: sectionsDict[roleSelectionForm.role],
        };
    }
);

export const getSdkEventStepCard = createSelector(
    [getSdkEvents, getDataRestrictions, getTranslate, getIsAgency],
    (sdkEvents, dataRestrictions, translate, isAgency) => {
        const sdkEventRestriction = dataRestrictions?.[SDK_EVENT_DIMENSION] || {};
        const selectedEventNames = sdkEventRestriction.values || [];
        const addFuture = sdkEventRestriction.addFuture || false;

        return {
            events: (sdkEvents || []).map(event => ({
                ...event,
                selected: selectedEventNames.includes(event.eventName),
            })),
            selectedEventNames,
            addFuture,
            translate,
            isAgency,
            loading: sdkEvents === null, // null = not loaded yet, [] = loaded but empty
        };
    }
);

export const getSourceSelectionPage = createSelector(
    [getUans, getPermissionDimension('base_source'), getWizardOverrideButtons, getTranslate],
    (uans, permissionDimension, wizardOverrideButtons, translate) => {
        return {
            uans: [
                [
                    {
                        ...organicSource,
                        selected: (permissionDimension?.selected.values || []).includes(organicSource.name),
                    },
                ],
                uniqBy(
                    Object.values(uans).map(u => ({
                        name: u.name,
                        displayName: u.display_name,
                        id: u.name,
                        icon: u.logo_path,
                        selected: (permissionDimension.selected.values || []).includes(u.name),
                    })),
                    'name'
                )
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .filter(s => s.name !== organicSource.name),
            ],
            wizardOverrideButtons,
            translate,
        };
    }
);

export const getGenericPermissionDimensionPage = dimensionName => {
    return createSelector(
        [getPermissionDimension(dimensionName), getWizardOverrideButtons, getTranslate],
        (permissionDimension, wizardOverrideButtons, translate) => {
            return {
                permissionDimension,
                wizardOverrideButtons,
                translate,
            };
        }
    );
};

export const getRoleSelectionFormPage = createSelector(
    [getWizard, getRoleSelectionForm, getEditedMember, getUserData, getTranslate],
    (wizard, roleForm, editedMember, userData, translate) => {
        return {
            mode: wizard.mode,
            role: roleForm.role,
            editedMember,
            showRestrictedMode: userData.is_permissions_premium,
            translate,
        };
    }
);

export const getConfigurationRequiredDialogPage = createSelector(
    [getConfigurationRequiredDialog, getTranslate],
    ({ open, pages }, translate) => {
        return {
            open,
            pages,
            translate,
        };
    }
);

const getFormattedFields = createSelector(
    [getFields, getFieldTypes, getSelectedMetrics],
    (fields, displayedFields, selectedMetrics) => {
        return displayedFields.map(field => ({
            ...field,
            values: fields[field.type]
                .filter(metric => rules.permissions(metric) && !EXCLUDED_METRICS_SELECTION_PAGE.includes(metric.name))
                .map(metric => ({
                    ...metric,
                    selected: selectedMetrics.values.includes(metric.name),
                })),
        }));
    }
);

export const getMetricsSelectionPage = createSelector(
    [getFormattedFields, getWizardOverrideButtons, getTranslate],
    (fields, wizardOverrideButtons, translate) => {
        return {
            fields,
            wizardOverrideButtons,
            translate,
        };
    }
);

export const getMetricsPageStep = createSelector([getFormattedFields], fields => {
    return {
        fields,
    };
});

const getScreensFormatted = createSelector(
    [getScreensDict, getSelectedMetrics, getMetricsSelectionPage, getTranslate, getRoleSelectionForm],
    (screensDict, selectedMetrics, metrics, translate, roleSelectionForm) => {
        const exportLogsTooltip = 'STATIC.PAGES.TEAM_MEMBERS.WIZARD_SCREENS_ENABLE_EXPORT_LOGS';
        const dataDestinationsTooltip = 'STATIC.PAGES.TEAM_MEMBERS.WIZARD_SCREENS_DISABLE_DATA_DESTINATIONS';
        const tooltip = {
            export_logs: exportLogsTooltip,
            'data-destinations': dataDestinationsTooltip,
        };
        return (screensDict[roleSelectionForm.role] || []).map(screen => ({
            name: screen.name,
            displayName: screen.display_name,
            children: screen.features.map(feature => {
                const metricsCount = metrics.fields.map(section => section.values).flat().length;
                const selectedMetricsCount = selectedMetrics.values.length;
                const shouldDisablePage =
                    (feature.name === 'export_logs' &&
                        (metricsCount !== selectedMetricsCount || !selectedMetrics.addFuture)) ||
                    feature.name === 'data-destinations';
                const disabledTooltip = tooltip[feature.name];
                return {
                    name: feature.name,
                    displayName: feature.display_name,
                    warning: shouldDisablePage ? null : feature.warning,
                    disabled: shouldDisablePage,
                    disabledTooltip: disabledTooltip ? translate(disabledTooltip) : null,
                };
            }),
        }));
    }
);

export const getScreenPageStep = createSelector(
    [getScreensFormatted, getScreenSelectionForm, getActiveMemberType],
    (screens, screenSelectionForm, activeMemberType) => {
        return {
            screens,
            screenSelectionForm,
            activeMemberType,
        };
    }
);

export const getScreenSelectionPage = createSelector(
    [getScreensFormatted, getScreenSelectionForm, getWizardOverrideButtons],
    (screens, screenSelectionForm, wizardOverrideButtons) => {
        const screensCopy = screens.map(section => {
            const newScreens = section.children
                .filter(s => s.name !== 'team_management')
                .map(screen => ({
                    ...screen,
                    selected: screenSelectionForm.selectedScreens.includes(screen.name),
                }));
            return {
                ...section,
                children: newScreens,
            };
        });
        return {
            screens: screensCopy,
            selectedScreens: screenSelectionForm.selectedScreens.filter(s => s !== 'team_management'),
            wizardOverrideButtons,
        };
    }
);

const getScreensForSummary = createSelector(
    [getScreensFormatted, getScreenSelectionForm],
    (screens, { selectedScreens, addFuture }) => {
        const ret = [];
        screens.forEach(section => {
            const selectedScreensData = section.children.filter(screen => selectedScreens.includes(screen.name));
            if (selectedScreensData.length) {
                ret.push({
                    ...section,
                    children: selectedScreensData,
                });
            }
        });
        return {
            values: ret,
            addFuture,
            overall: screens.flatMap(x => x.children).length,
        };
    }
);

const getFieldsForSummary = createSelector(
    [getMetricsSelectionPage, getSelectedMetrics],
    ({ fields }, { addFuture }) => {
        const ret = [];
        let overall = 0;
        fields.forEach(fieldType => {
            const selectedFields = fieldType.values.filter(f => f.selected);
            overall += fieldType.values.length;

            if (selectedFields.length) {
                ret.push({
                    ...fieldType,
                    values: selectedFields,
                });
            }
        });
        return {
            values: ret,
            addFuture,
            overall,
        };
    }
);

export const getSummaryViewPage = createSelector(
    [
        getUserRegistrationForm,
        getPermissionDimensions,
        getScreensForSummary,
        getFieldsForSummary,
        getWizard,
        getRoleSelectionForm,
        getSaveMemberError,
        getSummaryPages,
        getTranslate,
        getActiveMemberType,
    ],
    (
        registrationForm,
        permissionDimensions,
        screens,
        fields,
        wizard,
        roleSelectionForm,
        saveMemberError,
        pages,
        translate,
        activeMemberType
    ) => {
        const finishButtonText = translate(
            wizard.mode === WIZARD_MODES.edit
                ? 'STATIC.PAGES.TEAM_MEMBERS.WIZARD_UPDATE_PERMISSIONS_BUTTON'
                : `STATIC.BUTTONS.${activeMemberType}_CREATE_NEW_MEMBER`
        );

        return {
            registrationForm,
            permissionDimensions,
            screens,
            fields,
            wizard,
            roleSelectionForm,
            saveMemberError,
            pages,
            finishButtonText,
            allGroupsExpanded: activeMemberType === MEMBER_TYPES.AGENCY,
        };
    }
);

const getAllSubItems = (values, subItemKey) => {
    return values.reduce((total, parent) => {
        return total.concat(parent[subItemKey]);
    }, []);
};

export const getWizardFinished = createSelector(
    [
        getEditMemberId,
        getUserRegistrationForm,
        getAgencySelectionForm,
        getRoleSelectionForm,
        getMetricsSelectionPage,
        getSelectedMetrics,
        getScreenSelectionPage,
        getScreenSelectionForm,
        getPermissionDimensions,
        getActiveMemberType,
        getSectionPermissions,
        getDataRestrictions,
        getSdkEvents,
    ],
    (
        editMemberId,
        registrationForm,
        agencySelectionForm,
        roleForm,
        metricsSelectionPage,
        selectedMetrics,
        screenSelectionPage,
        selectedScreens,
        permissionDimensions,
        activeMemberType,
        sectionPermissions,
        dataRestrictionsState,
        allSdkEvents
    ) => {
        const roleType = roleForm.role;

        const allEventNames = (allSdkEvents || []).map(e => e.eventName);
        const dataRestrictions =
            activeMemberType === MEMBER_TYPES.AGENCY
                ? dataRestrictionsStateToApiFormat(dataRestrictionsState, allEventNames)
                : [];

        return {
            registrationForm:
                activeMemberType === MEMBER_TYPES.USER
                    ? {
                          name: registrationForm.firstName.value,
                          lastname: registrationForm.lastName.value,
                          email: registrationForm.email.value,
                          description: registrationForm.description.value,
                      }
                    : {
                          ...agencySelectionForm,
                      },
            role: {
                type: roleType,
                description: registrationForm.description.value,
                row: RESTRICTED_ROLES.includes(roleType)
                    ? Object.values(permissionDimensions).map(permDim => ({
                          dimension: permDim.name,
                          values: !permDim.selected.addFuture
                              ? permDim.selected.values
                              : difference(
                                    permDim.values.map(x => x.name),
                                    permDim.selected.values
                                ),
                          operator: permDim.selected.addFuture ? NOT_IN_OPERATOR : IN_OPERATOR,
                      }))
                    : [],
                column: {
                    operator: selectedMetrics.addFuture ? NOT_IN_OPERATOR : IN_OPERATOR,
                    values: !selectedMetrics.addFuture
                        ? selectedMetrics.values
                        : difference(
                              getAllSubItems(metricsSelectionPage.fields, 'values').map(i => i.name),
                              selectedMetrics.values
                          ),
                },
                features: {
                    operator: selectedScreens.addFuture ? NOT_IN_OPERATOR : IN_OPERATOR,
                    access_new_features: selectedScreens.addFuture,
                    values: !selectedScreens.addFuture
                        ? selectedScreens.selectedScreens
                        : difference(
                              getAllSubItems(screenSelectionPage.screens, 'children').map(x => x.name),
                              selectedScreens.selectedScreens
                          ),
                },
                sectionPermissions,
                dataRestrictions,
            },
            editMemberId,
        };
    }
);

const getInitialPermissionSnapshot = state => state.teamManagement.initialPermissionSnapshot;

export const getPermissionState = createSelector(
    [getInitialPermissionSnapshot, getWizardFinished],
    (snapshot, currentState) => ({
        startState: snapshot || currentState,
        currentState,
    })
);

export const getHasPermissionChanges = createSelector(
    [getPermissionState],
    ({ startState, currentState }) => !isEqual(currentState, startState)
);

export const getManagePermissionsPage = createSelector(
    [getWizard, getActiveMemberType, getAgencySelectionForm, getPage, getHasPermissionChanges],
    (wizard, activeMemberType, agencySelectionForm, page, hasChanges) => {
        return {
            mode: wizard.mode,
            activeMemberType,
            agencySelectionForm,
            page,
            hasChanges,
        };
    }
);

export const managePermissionsData = createSelector(
    [getMetricsSelectionPage, getScreenSelectionPage, getPermissionDimensions, getSdkEvents],
    (metricsSelectionPage, screenSelectionPage, permissionDimensions, sdkEventObjects) => {
        return {
            metrics: getAllSubItems(metricsSelectionPage.fields, 'values').map(i => i.name),
            screens: getAllSubItems(screenSelectionPage.screens, 'children').map(i => i.name),
            permissionDimensions,
            sdkEventNames: (sdkEventObjects || []).map(e => e.eventName),
        };
    }
);
